<template>
    <vx-table-row>
        <vx-table-row-title>
            <div class="flex items-center gap-2 min-w-0">
                <resource-name :modelValue="props.modelValue"></resource-name>
                <resource-details-popover :modelValue="{ ...props.modelValue, resource_type: 'campaign' }" :teamId="props.modelValue.team_id" :groupId="props.modelValue.group_id" :orgUnitId="props.modelValue.org_unit_id"></resource-details-popover>
            </div>
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> Status: </template>
            <vx-badge
                class="mr-4"
                size="xs"
                :color="CAMPAIGN_STATUSES[props.modelValue.status]?.badge"
                data-test="campaign-status"
                >{{ CAMPAIGN_STATUSES[props.modelValue.status]?.label }}</vx-badge
            >
        </vx-table-row-item>

        <vx-table-row-item data-test="contacts-count">
            <template v-slot:label> Contacts: </template>
            <font-awesome-icon
                v-if="showContactsLoading"
                :icon="faLoader"
                spin
                widthAuto
            ></font-awesome-icon>
            <vx-popover v-else-if="displayContactsCount != null" arrow hover placement="top" class="cursor-pointer" @click="copyText(formatNumber(displayContactsCount))">
                <span class="text-slate-500">{{ vxFormatCount(displayContactsCount) }}</span>
                <template v-slot:content>
                    <div class="text-center">
                        {{ formatNumber(displayContactsCount) }}
                        <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                    </div>
                </template>
            </vx-popover>
            <span v-else class="text-slate-500">-</span>
        </vx-table-row-item>

        <vx-table-row-item v-if="props.showCostColumn" data-test="cost">
            <template v-slot:label> Cost: </template>
            <font-awesome-icon
                v-if="!props.metrics"
                :icon="faLoader"
                spin
                widthAuto
            ></font-awesome-icon>
            <vx-popover v-else-if="props.metrics.estimatedCost != null" arrow hover placement="top" class="cursor-pointer" @click="copyText(currencyCents(props.metrics.estimatedCost))">
                <span class="text-slate-500">{{ vxFormatCentsAsDollars(props.metrics.estimatedCost, true) }}</span>
                <template v-slot:content>
                    <div class="text-center">
                        {{ currencyCents(props.metrics.estimatedCost) }}
                        <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                    </div>
                </template>
            </vx-popover>
            <span v-else class="text-slate-500">-</span>
        </vx-table-row-item>

        <vx-table-row-item data-test="send-on">
            <template v-slot:label> Send On: </template>
            <span class="text-slate-400" data-test="live-at">
                {{ props.modelValue.live_at ? toShortDateTimeRelativeDays(props.modelValue.live_at, getClientTz(), false) : '-' }}
            </span>
        </vx-table-row-item>

        <vx-table-row-buttons v-if="!props.readOnly">
            <vx-dropdown teleport placement="right">
                <vx-button color="muted" :outline="true" data-test="campaign-dropdown">
                    <font-awesome-icon fixedWidth :icon="faEllipsis"></font-awesome-icon>
                </vx-button>
                <template v-slot:items>
                    <router-dropdown-item :to="{ name: 'campaigns.new', query: { duplicate_from_id: props.modelValue.id } }"
                        :disabled="teamStatus !== 'active'" data-test="copy-btn">
                        Duplicate
                        <font-awesome-icon widthAuto :icon="faCopy" />
                    </router-dropdown-item>

                    <vx-dropdown-item :disabled="teamStatus !== 'active'" :data-test="`delete-btn`"
                        @click="destroy()">
                        Delete
                        <font-awesome-icon widthAuto :icon="faTrash" />
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>
            <vx-popover arrow placement="top" hover>
                <router-button color="muted"
                    :outline="true"
                    data-test="edit-btn"
                    block
                    :disabled="!['draft', 'scheduled'].includes(props.modelValue.status) || isEditBlockedByCorporate"
                    :to="{ name: 'campaigns.edit', params: { id: props.modelValue.id } }" class="grow lg:grow-0">
                    Edit
                    <font-awesome-icon widthAuto :icon="faFilePen"></font-awesome-icon>
                </router-button>
                <template v-if="isEditBlockedByCorporate" v-slot:content>
                    <div class="whitespace-pre-wrap text-center">{{ editBlockedMessage }}</div>
                </template>
                <template v-else-if="!['draft', 'scheduled'].includes(props.modelValue.status)" v-slot:content>
                    You can only edit when the status is <strong>draft</strong> or <strong>scheduled</strong>.
                </template>
            </vx-popover>

        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup>
import {
    faCopy,
    faEllipsis,
    faFilePen,
    faLoader,
    faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxTableRow,
    VxTableRowItem,
    VxTableRowTitle,
    VxTableRowButtons,
    vxFormatCentsAsDollars,
    vxFormatCount,
    VxSwal,
    VxDropdown,
    VxDropdownItem,
    VxPopover,
} from '@voxie/frontend-components';
import { computed } from 'vue';
import { campaignsClient } from '~/services';
import { getClientTz, toShortDateTimeRelativeDays } from '~/utils/date';
import { throwSwalRequestError } from '~/utils/request';
import ResourceName from '~/components/general/resources/ResourceName.vue';
import ResourceDetailsPopover from '~/components/general/resources/ResourceDetailsPopover.vue';
import { CAMPAIGN_STATUSES } from '~/constants/campaign';
import RouterDropdownItem from '~/components/general/RouterDropdownItem.vue';
import RouterButton from '~/components/general/RouterButton.vue';
import { capitalize, currencyCents, formatNumber } from '~/utils/helpers';
import { useCopyText } from '~/composables/useCopyText';

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
    marketingAllowed: {
        type: Boolean,
        default: true,
    },
    transactionalAllowed: {
        type: Boolean,
        default: true,
    },
    showCostColumn: {
        type: Boolean,
        default: false,
    },
    metrics: {
        type: Object,
        default: null,
    },
});

const emit = defineEmits(['deleted']);

const teamStatus = Spark.state.currentTeam.status;

const { copyText } = useCopyText();

const isEditBlockedByCorporate = computed(() => {
    if (props.modelValue.type === 'transactional' && !props.transactionalAllowed) {
        return true;
    }

    return props.modelValue.type === 'marketing' && !props.marketingAllowed;
});

const editBlockedMessage = computed(() => {
    if (
        (props.modelValue.type === 'transactional' && !props.transactionalAllowed) ||
        (props.modelValue.type === 'marketing' && !props.marketingAllowed)
    ) {
        return `Corporate has disabled the ability to create and edit ${capitalize(props.modelValue.type)} campaign.\nPlease contact Corporate for assistance.`;
    }

    return '';
});

const displayContactsCount = computed(() => props.metrics?.contactsCount);

const showContactsLoading = computed(() => !props.metrics);

const destroy = () => {
    VxSwal.confirmation(
        'Delete Campaign',
        "Are you sure? This campaign and its messages will be deleted. There is no undo.",
        'danger',
        () => {
            let deleteRequest;

            if (!props.modelValue.org_unit_id && !props.modelValue.group_id) {
                deleteRequest = campaignsClient.campaignDelete(
                    props.modelValue.team_id,
                    props.modelValue.id
                );
            } else if (props.modelValue.org_unit_id) {
                deleteRequest = campaignsClient.orgUnitCampaignDelete(
                    props.modelValue.team_id,
                    props.modelValue.org_unit_id,
                    props.modelValue.id
                );
            } else {
                deleteRequest = campaignsClient.groupCampaignDelete(
                    props.modelValue.team_id,
                    props.modelValue.group_id,
                    props.modelValue.id
                );
            }

            return deleteRequest
                .then(() => emit("deleted", props.modelValue.id))
                .catch(throwSwalRequestError);
        }
    );
};
</script>
